import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading.tsx";
import { removeEmptyStrings } from "@via/utils/helpers.tsx";
import { type ProfileClusterForClient } from "common";
import { JSX, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useUpdateOrganizationRunEnvironment } from "@via/pages/organization/run-environments/organization-run-environments.api.tsx";
import FormTextAreaComponent from "@via/components/inputs/form-textarea-component";

export default function OrganizationPremiumRunEnvironmentForm({
  setOpen,
  organizationId,
  editableRunEnvironment,
}: {
  setOpen: (open: boolean) => void;
  organizationId: string;
  editableRunEnvironment?: ProfileClusterForClient;
}): JSX.Element {
  const { control, register, handleSubmit } = useForm<ProfileClusterForClient>({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      variable: editableRunEnvironment?.variable || "",
      bashVariable: editableRunEnvironment?.bashVariable || "",
      spotInstance: editableRunEnvironment?.spotInstance || false,
    },
  });

  const {
    mutateAsync: updateRunEnvironment,
    isSuccess: isUpdateRunEnvironmentSuccess,
    isPending: isUpdateRunEnvironmentLoading,
  } = useUpdateOrganizationRunEnvironment();

  useEffect(() => {
    if (isUpdateRunEnvironmentSuccess) {
      setOpen(false);
    }
  }, [isUpdateRunEnvironmentSuccess]);

  const handleUpdateProfile = async (
    payload: ProfileClusterForClient
  ): Promise<void> => {
    if (editableRunEnvironment?.id) {
      await updateRunEnvironment({
        requestParams: {
          id: organizationId,
          runEnvironmentId: editableRunEnvironment?.id,
        },
        requestBody: {
          ...(removeEmptyStrings(payload) as ProfileClusterForClient),
          isSystemGenerated: editableRunEnvironment.isSystemGenerated,
        },
      });
    }
  };

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleUpdateProfile)(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              {editableRunEnvironment
                ? "Edit Run Environment"
                : "New Run Environment"}
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={() => {
                  setOpen(false);
                }}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              {editableRunEnvironment
                ? "Edit your run environment details below."
                : "Get started by filling in the information below to create your new run environment."}
            </p>
          </div>
        </div>

        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Run Environment Name <span className="text-red-400">*</span>
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    value={editableRunEnvironment?.name || ""}
                    disabled
                    className="w-full block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:bg-gray-200"
                    placeholder="Name your Profile"
                  />
                </div>
              </div>

              <FormTextAreaComponent
                control={control}
                name="variable"
                label="Profile Variables"
                placeholder="Your Profile Variables"
              />
              <FormTextAreaComponent
                control={control}
                name="bashVariable"
                label="Environment Variables"
                placeholder="Your Environment Variables"
              />
              <div className="flex items-center gap-2 mt-2">
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Spot Instance
                </label>
                <div>
                  <input
                    {...register("spotInstance")}
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="run-environment-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() => {
            setOpen(false);
          }}>
          Cancel
        </button>
        <button
          id="run-environment-save"
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isUpdateRunEnvironmentLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
}
